import React, { useState, useContext, useEffect, useReducer } from "react";
import _ from "lodash";
import { FormattedMessage, useIntl } from "react-intl";
import AuthenticationService from "../../services/AuthenticationService";
import Input from "../../ui-components/Input";
import Button from "../../ui-components/Button";
import Textarea from "../../ui-components/Textarea";
import { NotificationsContext } from "../../ui-components/Notifications";
import Collapsible from "../../ui-components/Collapsible";
import SearchSelect from "../../ui-components/SearchSelect";
import DatePicker from "../../ui-components/DatePicker";
import Toggle from "../../ui-components/Toggle";

import { useWorksiteUsers } from "../../hooks/useWorksiteUsers";
import useCountryAxiosInstance from "../../hooks/useCountryAxiosInstance";

import { useServicesChecklists } from "../../hooks/useServicesChecklists";
import { findOption, getUserLabel, allapproved, activitiesInfo } from "./utils";
import { formatDateStr } from "../../utils/date";

import ConfirmDelete from "../ConfirmDelete";
import Placeholder from "../../ui-components/Placeholder";
import { PencilIcon, PlusIcon } from "@heroicons/react/24/solid";
import {
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  PencilSquareIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";

import { activitiesReducer } from "./mainTaskActivitiesReducer";
import Label from "../../ui-components/Label";
import TaskMapModal from "./taskmap/TaskMapModal";
import { MainTaskActivitiesForm } from "./MainTaskActivitiesForm";
import WorksiteTaskExportModal from "../Modals/WorksiteTaskExportModal";
import { TaskStatusBadge } from "./TaskStatusBadge";
import WorksiteTaskActivity from "./WorksiteTaskActivity";
import {PRIVILEGED_ROLES} from "../../constants";

const MainTaskForm = ({
  worksite,
  changingExisting,
  worksite_task = {},
  activity_id = undefined,
  note_id = undefined,
  template_id = undefined,
  survey_id = undefined,
  onSubmit,
  onClose,
}) => {
  const intl = useIntl();
  const countryApiRequest = useCountryAxiosInstance();
  const { push } = useContext(NotificationsContext);
  const userData = AuthenticationService.getUserData();

  const [loading, setLoading] = useState(false);
  const [worksiteUsers] = useWorksiteUsers(worksite);
  const [checklists] = useServicesChecklists(worksite.id);
  const [form, setForm] = useState(worksite_task);
  const [stashedTaskmapState, setStashedTaskmapState] = useState(null);

  const [editMode, setEditMode] = useState(!changingExisting);
  const [originalForm, setOriginalForm] = useState(null);
  const [choosedWbsId, setchoosedWbsId] = useState(template_id);
  const [choosedSurveyId] = useState(survey_id);
  const [taskmapModalShow, setTaskmapModalShow] = useState(false);
  const [activities, dispatchActivities] = useReducer(activitiesReducer, {
    items: [],
  });

  const [showExportDialog, setShowExportDialog] = useState(false);
  const task_id_checklist = worksite_task?.id_checklist || null;
  const wbTaskIitem =
    checklists.length &&
    (choosedWbsId !== undefined || task_id_checklist !== null)
      ? checklists.find((w) => [choosedWbsId, task_id_checklist].includes(w.id))
      : null;

  const isPrivileged = [...PRIVILEGED_ROLES, "field"].includes(userData.role_type);

  // only new or if we are the owner
  const canModify =
    !worksite_task?.id ||
    (worksite_task?.id &&
      (worksite_task.id_owner === userData.id || isPrivileged));

  const viewOnly =
    !canModify && !isPrivileged && worksite_task.id_assignee !== userData.id;

  const activitiesNumbers = activitiesInfo(activities.items);

  const worksiteUsersOptions = _.sortBy(worksiteUsers, (i) => i.lastname).map(
    (i) => ({
      value: i.id,
      label: getUserLabel(i, intl),
    })
  );

  const mainWbsCategoriesOptions = checklists.map((c) => ({
    label: c.des_title,
    value: c.id,
    service: c.id_service,
  }));

  const canApproveTask =
    changingExisting &&
    canModify &&
    activities.items.length > 0 &&
    allapproved(activities.items);

  const isChangedTask = Object.entries(form).some(([key, value]) => {
    if (key === "modified_at" || key === "total_count") {
      return false;
    }
    return _.isEqual(value, worksite_task[key]) === false;
  });

  const showBottomBar =
    (editMode && canModify === true) || canApproveTask;
  const bottomBarHeight = showBottomBar
    ? canApproveTask
      ? "160px"
      : "60px"
    : "0";

  /**
   * ACTIVITIES
   */
  useEffect(() => {
    const fetchactivities = async () => {
      const activities = await countryApiRequest.get(
        "/worksites_tasks_activities/byidtask?id_task=" + worksite_task.id
      );
      dispatchActivities({
        type: "set_activities",
        activities,
      });
    };
    if (worksite_task?.id) {
      fetchactivities();
    }
  }, [
    worksite_task?.id,
    countryApiRequest,
    userData.firstname,
    userData.lastname,
  ]);

  const createOrUpdateActivities = async (
    id_task,
    activities,
    notify = true
  ) => {
    try {
      if (id_task === undefined || id_task === null) {
        return;
      }
      setLoading(true);
      if (activities?.length) {
        const payload = activities.map((a) => ({
          ...a,
          id_task,
        }));
        const result = await countryApiRequest.post(
          `/worksites_tasks_activities/fortask?notify=${notify}`,
          {
            id_task,
            activities: payload,
          }
        );
        dispatchActivities({
          type: "set_activities",
          activities: result,
        });
        onSubmit(false, notify);
        return result;
      }
    } catch (e) {
      push({
        type: "error",
        title: intl.formatMessage({ id: "an_error_occurred" }),
      });
    } finally {
      setLoading(false);
    }
    return null;
  };

  useEffect(() => {
    if (!changingExisting && choosedSurveyId) {
      setForm((f) => ({
        ...f,
        id_survey: choosedSurveyId,
      }));
    }
    if (!changingExisting && choosedWbsId && checklists.length) {
      async function getactivities() {
        const params = { asc: "val_order" };
        const items = await countryApiRequest.get(
          `/services_checklists_items/by_checklists_ids/${choosedWbsId}`,
          { params }
        );
        const selectedactivities = items.map((a) => ({
          id_checklist_item: a.id,
          val_text: a.des_title,
          val_description: a.des_description || "",
          activity_type: "checklists",
          val_creator: "",
        }));
        const wbsitem = checklists.find((w) => w.id === choosedWbsId);
        setForm((f) => ({
          ...f,
          id_checklist: wbsitem.id,
          des_name: wbsitem.des_title || "",
          des_description: wbsitem.des_description || "",
        }));
        dispatchActivities({
          reset: true,
          type: "add_activities",
          activities: selectedactivities,
          owner: `${userData.firstname} ${userData.lastname}`,
        });
      }
      getactivities();
    }
  }, [
    changingExisting,
    choosedWbsId,
    choosedSurveyId,
    checklists,
    userData.firstname,
    userData.lastname,
    countryApiRequest,
  ]);

  const saveStashedTaskmapState = async (task, taskmapState) => {
    if (taskmapState && Object.values(taskmapState).length) {
      return await countryApiRequest.post(`/worksites_tasks_maps_data/`, {
        id_task: task.id,
        id_taskmap: taskmapState.taskMapId,
        canvas_data: taskmapState.canvas_data,
      });
    }
    return null;
  };

  /**
   * TASK HANDLING
   */
  const createOrUpdateTask = async (form, taskmapState) => {
    try {
      setLoading(true);
      let task;
      const payload = Object.assign({}, form);
      payload.modified_at = new Date().toISOString();
      if (worksite_task?.id) {
        task = await countryApiRequest.put(
          `/worksites_tasks/${worksite_task.id}`,
          payload
        );
      } else {
        payload.id_owner = userData.id;

        payload.id_worksite = worksite.id;
        payload.flg_approved =
          payload.flg_approved === undefined ? false : payload.flg_approved;
        payload.r_perm = ["public"];
        payload.w_perm = ["admin", "CS", "field", `user_${userData.id}`];
        task = await countryApiRequest.post("/worksites_tasks", payload);
      }
      // on first task save also save associated activities
      if (!worksite_task?.id && task?.id && activities?.items.length) {
        await createOrUpdateActivities(task.id, activities.items, false);
      }
      await saveStashedTaskmapState(task, taskmapState);
      return task;
    } catch (e) {
      push({
        type: "error",
        title: intl.formatMessage({ id: "an_error_occurred" }),
      });
    } finally {
      setLoading(false);
    }
    return null;
  };

  const deleteSelectedTask = async (taskToDelete) => {
    if (!taskToDelete) return;

    try {
      await countryApiRequest.delete(`/worksites_tasks/${taskToDelete.id}`);
      onSubmit(true, false);
      push({
        type: "info",
        title: intl.formatMessage({
          id: "worksite_task_removed_successfully",
        }),
      });
    } catch (error) {
      push({
        type: "error",
        title: intl.formatMessage({ id: "an_error_occurred" }),
      });
      throw error;
    }
  };

  /**
   * TASKMAP HANDLING
   */
  const deleteTaskMapData = async (id_task_map) => {
    if (id_task_map && worksite_task.id) {
      try {
        await countryApiRequest.post("/worksites_tasks_maps_data/delete/", {
          id_taskmap: id_task_map,
          id_task: worksite_task.id,
        });
        setForm({ ...form, id_task_map: null });
        onSubmit(false);
        push({
          type: "info",
          title: intl.formatMessage({
            id: "worksite_task_mapdata_removed_successfully",
          }),
        });
      } catch (error) {
        push({
          type: "error",
          title: intl.formatMessage({ id: "an_error_occurred" }),
        });
        throw error;
      }
    }
  };

  return (
    <>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          await createOrUpdateTask(form, stashedTaskmapState);
          onSubmit(true);
        }}
        style={{ minHeight: 0 }}
      >
        <div
          style={{
            height: showBottomBar ? `calc(100% - ${bottomBarHeight})` : "100%",
            overflowY: "scroll",
          }}
          className="px-5 pt-2 pb-10 flex flex-col gap-4 relative"
        >
          {editMode !== null && canModify && !!worksite_task?.id && (
            <div className="flex justify-between gap-3">
              <div className="flex gap-3">
                {editMode === false && (
                  <>
                    <Button
                      onClick={() => {
                        setEditMode(true);
                        setOriginalForm(form);
                      }}
                    >
                      <PencilSquareIcon className="w-4" />
                      <FormattedMessage id="edit" />
                    </Button>
                    <Button
                      type="button"
                      styleType="empty"
                      onClick={() => {
                        setShowExportDialog(true);
                      }}
                    >
                      <ArrowDownTrayIcon className="w-4" />
                      <FormattedMessage id="export" />
                    </Button>
                  </>
                )}
                {editMode === true && (
                  <Button
                    styleType="empty"
                    onClick={() => {
                      setForm(originalForm); // discard changes
                      setOriginalForm(null);
                      setEditMode(false);
                    }}
                  >
                    <XMarkIcon className="w-4" />
                    <FormattedMessage id="cancel" />
                  </Button>
                )}
              </div>
              <div className="flex-shrink-0">
                <ConfirmDelete
                  onConfirm={() => deleteSelectedTask(worksite_task)}
                  confirmText={intl.formatMessage({ id: "confirm" })}
                  dismissText={intl.formatMessage({ id: "cancel" })}
                />
              </div>
            </div>
          )}

          <div className="flex gap-3">
            {wbTaskIitem && wbTaskIitem?.service?.name && (
              <div className="my-auto text-black-500 font-semibold text-xs tracking-wide">
                # {wbTaskIitem?.service?.name}
              </div>
            )}
            {worksite_task?.id && worksite_task.id_survey && (
              <div className="my-auto">
                <div className="text-xs">
                  <span className="font-semibold">
                    <FormattedMessage id="configuration" />
                  </span>
                  :&nbsp;
                  {worksite_task?.survey_name || (
                    <FormattedMessage id="unnamed_configuration" />
                  )}
                </div>
              </div>
            )}
            {!editMode && worksite_task?.id && (
              <div className="my-auto">
                <TaskStatusBadge status={worksite_task.status} />
              </div>
            )}
          </div>

          <div className={!template_id ? "hidden" : ""}>
            {editMode &&
              !!canModify &&
              (!worksite_task?.id || !form.id_checklist) &&
              (mainWbsCategoriesOptions.length ? (
                <SearchSelect
                  value={findOption(
                    form.id_checklist,
                    mainWbsCategoriesOptions
                  )}
                  label={intl.formatMessage({ id: "task_form_wbs" })}
                  options={mainWbsCategoriesOptions}
                  isDisabled={template_id !== undefined}
                  isClearable={template_id === undefined}
                  filterOption={(candidate, user_input) => {
                    // Filter options that also have parent label matching the input string
                    const input = user_input.toLowerCase();
                    return (
                      candidate.data.parent_label
                        ?.toLowerCase()
                        .includes(input.toLowerCase()) ||
                      candidate.label
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    );
                  }}
                  onChange={(selected) => {
                    setchoosedWbsId(selected?.value);
                    const data = {
                      id_checklist: selected?.value,
                    };
                    if (!selected) {
                      // if cleared, reset also title and description
                      data.des_name = undefined;
                      data.des_description = undefined;
                    }
                    setForm({ ...form, ...data });
                  }}
                />
              ) : (
                <Placeholder height="h-16" width="w-full" />
              ))}
          </div>

          <div className="w-full">
            {(!editMode || (!canModify && form.des_name)) && (
              <div className="flex flex-col gap-2">
                <div className="font-semibold">
                  {intl.formatMessage({ id: "task_name" })}
                </div>
                <div className="flex gap-4">
                  <div>{form.des_name}</div>
                </div>
              </div>
            )}
            {editMode && !!canModify && (
              <Input
                value={form.des_name || ""}
                label={intl.formatMessage({ id: "task_form_name" })}
                disabled={form.id_checklist || template_id !== undefined}
                onChange={(e) => {
                  setForm({ ...form, des_name: e.target.value });
                }}
                // placeholder={intl.formatMessage({ id: "task_form_name" })}
                // errorMessage={intl.formatMessage({ id: "task_form_enter_name" })}
                required
              />
            )}
          </div>
          <div className="w-full">
            {(!editMode || (!canModify && form.des_description)) && (
              <div className="flex flex-col gap-2">
                <div className="font-semibold">
                  {intl.formatMessage({ id: "task_description" })}
                </div>
                <div>{form.des_description}</div>
              </div>
            )}
            {editMode && canModify && (
              <Textarea
                value={form.des_description || ""}
                label={intl.formatMessage({ id: "task_form_description" })}
                disabled={form.id_checklist || template_id !== undefined}
                onChange={(e) => {
                  setForm({ ...form, des_description: e.target.value });
                }}
                textCls="text-sm"
                // placeholder={intl.formatMessage({
                //   id: "task_form_description",
                // })}
                required
              />
            )}
          </div>
          <div className="w-full">
            {!editMode &&
              (worksiteUsers.length ? (
                <div className="flex gap-4">
                  <div className="flex flex-col gap-2">
                    <div className="font-semibold">
                      {intl.formatMessage({ id: "task_assignee" })}
                    </div>
                    <div>
                      {worksiteUsers.length
                        ? getUserLabel(
                            worksiteUsers.find(
                              (w) => w.id === form.id_assignee
                            ),
                            intl
                          )
                        : ""}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="font-semibold">
                      {intl.formatMessage({ id: "task_owner" })}
                    </div>
                    <div>
                      {getUserLabel(
                        worksiteUsers.find((w) => w.id === form.id_owner),
                        intl
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <Placeholder height="h-16" width="w-full" />
              ))}
            {editMode &&
              canModify &&
              (worksiteUsersOptions ? (
                <SearchSelect
                  value={findOption(form.id_assignee, worksiteUsersOptions)}
                  label={intl.formatMessage({ id: "task_form_assignee" })}
                  options={worksiteUsersOptions}
                  onChange={(selected) =>
                    setForm({ ...form, id_assignee: selected.value })
                  }
                  required
                />
              ) : (
                <Placeholder height="h-16" width="w-full" />
              ))}
          </div>
          <div className="w-full">
            {!editMode && worksite_task?.id && (
              <div className="flex flex-col gap-2">
                <div className="font-semibold">
                  {intl.formatMessage({ id: "last_update" })}
                </div>
                <p>{formatDateStr(worksite_task.modified_at)}</p>
              </div>
            )}
          </div>
          <div className="w-full">
            {(!editMode || !canModify) && form.expires_at && (
              <div className="flex flex-col gap-2">
                <div className="font-semibold">
                  {intl.formatMessage({ id: "task_form_expires_at" })}
                </div>
                <div>
                  {form.expires_at ? formatDateStr(form.expires_at) : ""}
                </div>
              </div>
            )}
            {editMode && canModify && (
              <DatePicker
                value={form.expires_at ? new Date(form.expires_at) : undefined}
                showErrorDialog={false}
                onChange={(e) => {
                  setForm({ ...form, expires_at: e });
                }}
                label={intl.formatMessage({ id: "task_form_expires_at" })}
                placeholder={intl.formatMessage({ id: "select_date" })}
                errorMessage={intl.formatMessage({ id: "select_date" })}
                minDate={
                  form.created_at ? new Date(form.created_at) : new Date()
                }
                calendarDirection={"top"}
              />
            )}
          </div>

          <div className="w-full">
            {(!editMode || (!canModify && form.expires_at)) && (
              <div className="flex flex-col gap-2">
                <div className="font-semibold">
                  {intl.formatMessage({ id: "task_map" })}
                </div>
                <div>
                  {!!form.id_task_map ? (
                    <div
                      className="w-full cursor-pointer text-kerakoll-700 font-medium hover:underline"
                      onClick={() => {
                        setTaskmapModalShow(true);
                      }}
                    >
                      <ArrowTopRightOnSquareIcon className="w-4 inline mr-1.5 mb-0.5" />
                      <FormattedMessage id="view_on_map" />
                    </div>
                  ) : (
                    <FormattedMessage id="none_f" />
                  )}
                </div>
              </div>
            )}
            {editMode && canModify && (
              <div className="mt-1">
                <Label>
                  <FormattedMessage id="task_map" />
                </Label>
                <div className="mt-1.5 flex gap-5">
                  {!stashedTaskmapState && (
                    <Button
                      type="button"
                      styleType={!!form.id_task_map ? "default" : "empty"}
                      textCls="text-xs"
                      onClick={() => {
                        setTaskmapModalShow(true);
                      }}
                    >
                      {!!form.id_task_map ? (
                        <>
                          <PencilIcon className="w-3" />
                          <FormattedMessage id="modify_existing" />
                        </>
                      ) : (
                        <>
                          <PlusIcon className="w-3" />
                          <FormattedMessage id="add_taskmap" />
                        </>
                      )}
                    </Button>
                  )}
                  {!!form.id_task_map && !stashedTaskmapState ? (
                    <ConfirmDelete
                      onConfirm={() => deleteTaskMapData(form.id_task_map)}
                      confirmText={intl.formatMessage({ id: "confirm_delete" })}
                      dismissText={intl.formatMessage({ id: "cancel" })}
                    />
                  ) : (
                    <></>
                  )}
                  {stashedTaskmapState && !form.id_task && (
                    <div className="flex flex-col gap-2 w-full">
                      <div className="flex justify-between gap-5 w-full">
                        <Button
                          type="button"
                          styleType={"default"}
                          textCls="text-xs"
                          onClick={() => {
                            setTaskmapModalShow(true);
                          }}
                        >
                          <PencilIcon className="w-3" />
                          <FormattedMessage id="modify_existing" />
                        </Button>
                        <ConfirmDelete
                          onConfirm={() => {
                            setStashedTaskmapState(null);
                            setForm({ ...form, id_task_map: null });
                          }}
                          confirmText={intl.formatMessage({
                            id: "confirm_delete",
                          })}
                          dismissText={intl.formatMessage({ id: "cancel" })}
                        />
                      </div>
                      {!worksite_task?.id && (
                        <div>
                          <FormattedMessage id="save_task_to_persist_changes" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <Collapsible
            defaultOpen={
              editMode || (activitiesNumbers?.total ?? 0) < 10 || !!activity_id
            }
            title={
              <div className="flex items-center gap-3">
                <h2 className="leading-none">
                  <FormattedMessage id="task_activities" />
                </h2>{" "}
                <div className="flex items-center justify-center bg-ag-600 rounded-full py-1 px-2 text-xs text-white leading-none">
                  {activitiesNumbers.numdone} / {activitiesNumbers.total}
                </div>
              </div>
            }
            classNames="mt-6 mb-3 text-lg font-semibold"
          >
            {!viewOnly && (
              <>
                <MainTaskActivitiesForm
                  activities={activities}
                  worksite={worksite}
                  task={worksite_task}
                  loading={loading}
                  editMode={editMode}
                  onSubmit={(content, activity, index) => {
                    const action = {
                      type: "edit_activity",
                      index,
                      ...content,
                    };
                    const newactivities = activitiesReducer(
                      activities,
                      action
                    ).items.filter((na) => na.id === activity.id);
                    dispatchActivities(action);
                    createOrUpdateActivities(worksite_task?.id, newactivities);
                  }}
                  onSkip={(skipped, activity, index) => {
                    const action = {
                      type: "skip_activity",
                      index,
                      skipped,
                    };
                    const newactivities = activitiesReducer(
                      activities,
                      action
                    ).items.filter((na) => na.id === activity.id);
                    dispatchActivities(action);
                    createOrUpdateActivities(worksite_task?.id, newactivities);
                  }}
                  onCheck={(checked, activity, index) => {
                    const action = {
                      type: "check_activity",
                      index,
                      closed_by: `${userData.firstname} ${userData.lastname}`,
                      checked,
                    };
                    const newactivities = activitiesReducer(
                      activities,
                      action
                    ).items.filter((na) => na.id === activity.id);
                    dispatchActivities(action);
                    createOrUpdateActivities(worksite_task?.id, newactivities);
                  }}
                  onDelete={(to_delete, activity, index) => {
                    const action = {
                      type: "delete_activity",
                      index,
                      to_delete,
                    };
                    const newactivities = activitiesReducer(
                      activities,
                      action
                    ).items.filter((na) => na.id === activity.id);
                    dispatchActivities(action);
                    createOrUpdateActivities(worksite_task?.id, newactivities);
                  }}
                  onDrop={(activities, updatedActivities) => {
                    const payload = {
                      type: "set_activities",
                      activities: updatedActivities,
                    };
                    dispatchActivities(payload);
                    const newActivities = activitiesReducer(
                      activities,
                      payload
                    );
                    createOrUpdateActivities(
                      worksite_task?.id,
                      newActivities.items
                    );
                  }}
                  activity_id={activity_id}
                  note_id={note_id}
                />
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    dispatchActivities({
                      type: "add_activities",
                      activities: [{}],
                      owner: `${userData.firstname} ${userData.lastname}`,
                    });
                  }}
                >
                  <PlusIcon className="w-4" />
                  <FormattedMessage id="add_checklist_activity" />
                </Button>
              </>
            )}
            {viewOnly && activities?.items.length > 0 && (
              <div className="mt-3">
                {activities.items.map((a, index) => (
                  <div className="my-3" key={index}>
                    <WorksiteTaskActivity
                      title={a.val_text}
                      description={a.val_description}
                      worksite={worksite}
                      isDraft={!a.id}
                      viewOnly={viewOnly}
                      editable={false}
                      activityType={a.activity_type}
                      canMarkAsDone={false}
                      activityId={a.id}
                      skipped={a.flg_disabled}
                      deleted={a.to_delete}
                      checked={a.closed_at}
                      completedBy={a.val_closed_by}
                      completedOn={a.closed_at}
                      createdBy={a.val_creator}
                      note_id={a.id === activity_id ? note_id : undefined}
                    />
                  </div>
                ))}
              </div>
            )}
          </Collapsible>
        </div>
        {showBottomBar && (
          <div
            className="py-3 px-8 flex justify-content-center flex-col shadow shadow-inner shadow-xxl my-auto"
            style={{ height: bottomBarHeight }}
          >
            {canApproveTask && (
              <div className="mb-4">
                <Toggle
                  label={intl.formatMessage({ id: "task_form_approved" })}
                  checked={form.flg_approved}
                  onChange={() =>
                    setForm({ ...form, flg_approved: !form.flg_approved })
                  }
                />
              </div>
            )}
            {isChangedTask && (
              <Button
                styleType="darkGreen"
                full
                type="submit"
                submitting={loading}
              >
                <CheckIcon className="w-4 mr-1" />
                <FormattedMessage
                  id={!!worksite_task?.id ? "task_update" : "task_create"}
                />
              </Button>
            )}
            {!isChangedTask && (
              <Button
                styleType="darkGreen"
                full
                onClick={(e) => {
                  e.preventDefault();
                  onClose();
                }}
              >
                <CheckIcon className="w-4 mr-1" />
                <FormattedMessage id={"task_close_confirm"} />
              </Button>
            )}
          </div>
        )}
      </form>

      {taskmapModalShow && (
        <TaskMapModal
          taskMapId={form.id_task_map}
          taskId={worksite_task?.id}
          workditeId={worksite.id}
          showControls={!form.id_task_map}
          editMode={!!taskmapModalShow && editMode}
          initialState={stashedTaskmapState}
          onExit={() => {
            setTaskmapModalShow(false);
          }}
          onSave={async (s) => {
            if (s && Object.values(s).length && !worksite_task.id_task_map) {
              setStashedTaskmapState(s);
            }
            const newform = { ...form, id_task_map: s.taskMapId };
            if (worksite_task?.id) {
              // automatically save taskmap data on existing task
              createOrUpdateTask(newform, s);
              onSubmit(false);
            }
            setForm(newform);
            setTaskmapModalShow(false);
          }}
        />
      )}
      {showExportDialog && (
        <WorksiteTaskExportModal
          worksite={worksite}
          worksiteUsers={worksiteUsers}
          task={worksite_task}
          activities={activities}
          onExit={() => {
            setShowExportDialog(false);
          }}
        />
      )}
    </>
  );
};

export default MainTaskForm;
